import { memo } from 'react';
import { Link } from '@/hen-ui/atomics/Link';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
import type { NavItem, SubNavPanelStyle } from './types';
import { ColumnLabel } from './ColumnLabel';
import { resolveNavLinkProps } from './subnav.constants';
import { subNavLinkColorClass, subNavLinkListClass } from './subnav.variants';
import { hasValidHref } from './utils';

interface NavColumnProps {
  column: NavItem;
  panelStyle?: SubNavPanelStyle;
}

export const NavColumn = memo(function NavColumn({
  column,
  panelStyle,
}: NavColumnProps) {
  const links = (column.children ?? []).filter(
    (child) => child.type !== 'action',
  );
  const linkColor = subNavLinkColorClass(panelStyle);
  const linkHover = panelStyle?.linkHover;

  return (
    <Flex direction='column' gap='none'>
      <ColumnLabel item={column} panelStyle={panelStyle} />
      {links.length > 0 && (
        <List
          bulletStyle='none'
          className={cn(
            'gap-lg pt-xs lg:pt-xxs xl:pt-xs',
            subNavLinkListClass(panelStyle),
          )}
        >
          {links.map((link) => {
            const props = resolveNavLinkProps(
              link,
              panelStyle?.linkStyle ?? 'subtext',
              linkHover,
            );
            const isSubtext = props.variant === 'subtext';
            return (
              <ListItem
                key={link.id}
                className='text-[length:inherit] leading-[inherit]'
              >
                {hasValidHref(link) ? (
                  <Link
                    href={link.uri}
                    variant={props.variant}
                    size={props.size}
                    underline='none'
                    underlineStyle='thin'
                    className={cn(
                      props.className,
                      isSubtext && linkColor,
                      'text-[length:inherit] leading-[inherit]',
                    )}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <span
                    className={cn(
                      props.variant === 'subtext'
                        ? linkColor || 'text-subtext'
                        : 'text-black',
                      'text-[length:inherit] leading-[inherit]',
                    )}
                  >
                    {link.label}
                  </span>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Flex>
  );
});
